import React, { useMemo } from 'react';
import { Row, Col, Text, Button, FlexboxGrid } from 'rsuite';
import {
  getAddressWithUnitNumber,
  isNullUndefinedOrEmptyString,
  titleize,
} from '../../../common/utils';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { C_PERMIT_STATUS_CURRENT } from '../../../common/consts';
import Badge from '../../common/Badge';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../../store/slices/config/configSelectors';
import { License, getLicenseTypeLabels } from 'src/types/licenses';

interface UserLicenseRowProps {
  license: License;
  taxCertificateEnabled?: boolean;
}

const UserLicenseRow = ({
  license,
  taxCertificateEnabled = false,
}: UserLicenseRowProps) => {
  const placeNavigate = usePlaceNavigate();
  const typeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);
  const licenseTypeLabels = getLicenseTypeLabels(typeVerbiage);

  const fullAddress = useMemo(() => {
    return getAddressWithUnitNumber(
      license.property_address,
      license.unit_number
    );
  }, [license]);

  const { expirationDateOrStatus, isExpirationDateOrStatusItalic } =
    useMemo(() => {
      if (license.status !== C_PERMIT_STATUS_CURRENT) {
        return {
          expirationDateOrStatus: titleize(license.status),
          isExpirationDateOrStatusItalic: true,
        };
      }

      const expDate = license.expiration_date;
      if (isNullUndefinedOrEmptyString(expDate)) {
        return {
          expirationDateOrStatus: 'Expiration: -',
          isExpirationDateOrStatusItalic: false,
        };
      }

      const isValidDate = dayjs(expDate).isValid();
      return {
        expirationDateOrStatus: isValidDate
          ? `Expiration: ${dayjs(expDate).format('YYYY-MM-DD')}`
          : titleize(expDate),
        isExpirationDateOrStatusItalic: !isValidDate,
      };
    }, [license]);

  return (
    <FlexboxGrid
      as={Row}
      key={`license_${license.license_else_application_number}`}
      gutter={10}
      align="middle"
      style={{ minHeight: '61px' }}
    >
      <FlexboxGrid.Item
        role="cell"
        as={Col}
        xs={19}
      >
        <div className="ph-4 pv-4">
          <Text className="text-bold mb-4">{titleize(fullAddress)}</Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: '2px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {taxCertificateEnabled && license.license_type && (
                <Badge label={licenseTypeLabels[license.license_type]} />
              )}
              <Text>{license.license_else_application_number}</Text>
            </div>
            <Text
              as={isExpirationDateOrStatusItalic ? 'i' : 'span'}
              style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
            >
              {expirationDateOrStatus}
            </Text>
          </div>
        </div>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        className="pr-12"
        as={Col}
        xs={5}
        style={{ display: 'flex', justifyContent: 'flex-end' }}
      >
        <Button
          className="btn-secondary"
          onClick={() => {
            placeNavigate(`/single-permit/${license.application_number}`);
          }}
          data-cy="single-permit-button"
        >
          View
        </Button>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default UserLicenseRow;
